import React from 'react';
import { Button, Popup } from 'semantic-ui-react';
import { getRiskColumnsByLanguage, fiveByFiveLikelihoodOptions, sixBySixLikelihoodOptions } from '../helpers';
import { riskRatingFormatter, riskRatingClasses, likelihoodCellFormatter } from '../helpers/risk_helpers';
import ControlsRenderer from './controls_component';
import ActionStatusRenderer from './action_status_renderer';
import { getText } from '../helpers';

const createRatingColumn = (dataField, text, isHidden = false) => ({
  dataField,
  text,
  headerStyle: {
    width: '100px',
    textAlign: 'center',
  },
  title: true,
  align: 'center',
  headerClasses: 'column-color-ratings',
  classes: 'column-color-ratings',
  hidden: isHidden,
  editor: {
    type: 'modal'
  },
});

const consequenceTypes = (translations, isFiveByFiveMatrix) => [
  { dataField: 'financial', text: translations.financial },
  { dataField: 'safety', text: translations.safety },
  { dataField: 'health', text: translations.occ_health, isHidden: !isFiveByFiveMatrix },
  { dataField: 'hygiene', text: getText('Hygiene'), isHidden: isFiveByFiveMatrix },
  { dataField: 'reputation', text: translations.reputation },
  { dataField: 'environmental', text: translations.environment },
  { dataField: 'legal', text: translations.legal },
  { dataField: 'community', text: translations.community },
];

const controlsCellFormatter = (cell, row, planId, controlType, translations, TagOptions) => {
  const permission = { can_update: false };
  return <ControlsRenderer
          value={cell}
          planId={planId}
          riskId={row.id}
          controlType={controlType}
          translations={translations}
          permission={permission}
          setTagOptions={() => {}}
          TagOptions={TagOptions}
          skipFetchingDeletedControlNotes={true}
          />
}

const actionStatusRenderer = (cell, row, translations) => {
  return (
    <ActionStatusRenderer
      details= { cell }
      translations={translations}
      permission={{can_update: false}}
    />
  )
}

const cellFormatter = (cell, row, rowIndex) => {
  return (
   <span className={ 'cell-style' } cell={ cell } row={ rowIndex }>
    { cell }
   </span>
  );
}

const planCellFormatter = (row, onCopyUnwantedEventData, permission) => {
  const disableCopyPaste = !(permission && permission.can_update);
  return (
      <div className="plan-cell-formatter">
        <a href={`/plan/${row.plan.id}`} target="_blank" rel="noopener noreferrer">
            {row.plan.name}
        </a>
        <Popup
          trigger={ 
            <Button primary className="copy-unwanted-event-data-button" onClick={() => onCopyUnwantedEventData(row)} disabled={disableCopyPaste}>
              {getText("Copy - Paste")}
            </Button>
          }
          content= {getText("Copy this unwanted event data and paste it to the unwanted event in the current plan.")}
          flowing
        />
      </div>
  )
}

export const getColumns = (props) => {
  const { translations, languageName, riskRatingDict, TagOptions, instanceConfig, onCopyUnwantedEventData, permission } = props;
  const { risk_matrix: riskMatrix = '5x5', risk_matrices={} } = instanceConfig;
  const isFiveByFiveMatrix = riskMatrix === '5x5';
  const sixBySixRiskMatrix = risk_matrices['6x6'] || [];
  const keyPrefix = isFiveByFiveMatrix ? '' : '6x6_';
  const { work_phase: work_phase_key, activity: activity_key, hazard: hazard_key, unwanted_event: unwanted_event_key } = getRiskColumnsByLanguage(languageName);
  
  const ratingColumns = consequenceTypes(translations, isFiveByFiveMatrix).map(c =>
    createRatingColumn(`${keyPrefix}${c.dataField}`, c.text, c.isHidden)
  );

  const newRatingColumns = consequenceTypes(translations, isFiveByFiveMatrix).map(c =>
    createRatingColumn(`${keyPrefix}new_${c.dataField}`, c.text, true)
  );

  const riskRatingDeps = { isFiveByFiveMatrix, riskRatingDict, sixBySixRiskMatrix };
  const likelihoodOptions = isFiveByFiveMatrix ? fiveByFiveLikelihoodOptions() : sixBySixLikelihoodOptions();

  return [
    {
      dataField: 'plan.name',
      text: getText('Plan Name'),
      formatter: (cell, row) => planCellFormatter(row, onCopyUnwantedEventData, permission),
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '200px',
        textAlign: "center",
      },
      headerTitle: true,
      title: true,
      align: 'center',
    },
    {
      dataField: work_phase_key,
      text: translations.work_phase_or_ambient_exposure ,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '200px',
        textAlign: "center",
      },
      headerTitle: true,
      title: true,
      align: 'center',
      headerClasses: 'column-color-workphase',
      classes: 'column-color-workphase work-phase-cell',
      hidden: true,
    },
    {
      dataField: activity_key,
      text: translations.activity,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '180px',
        textAlign: "center",
      },
      headerTitle: true,
      title: true,
      align: 'center',
      headerClasses: 'column-color-activity',
      classes: 'column-color-activity activity-cell',
      hidden: true,
    },
    {
      dataField: hazard_key,
      text: translations.hazard,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '180px',
        textAlign: "center",
      },
      headerTitle: true,
      title: true,
      align: 'center',
      headerClasses: 'column-color-hazard-event',
      classes: 'column-color-hazard-event hazard-cell',
      hidden: true,
    },
    {
      dataField: unwanted_event_key,
      text: translations.unwanted_event,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '180px',
        textAlign: "center",
      },
      headerTitle: true,
      title: true,
      align: 'center',
      headerClasses: 'column-color-hazard-event',
      classes: 'column-color-hazard-event unwanted-event-cell',
      hidden: true,
    },
    {
      dataField: 'existing_preventative',
      text: translations.existing_preventative_controls,
      formatter: (cell, row)=>controlsCellFormatter(cell, row, row.plan.id, 'existing-preventative', translations, TagOptions),
      editor: {
        type: 'custom'
      },
      headerTitle: true,
      headerStyle: {
        width: '300px',
        textAlign: "center",
      },
      align: 'center',
      headerClasses: 'column-color-controls',
      classes: 'column-color-controls',
      cellStyle: {
        width: '300px',
        paddingBottom: '10px'
      }
    },
    {
      dataField: 'existing_recovery',
      text: translations.existing_recovery_controls,
      formatter: (cell, row)=>controlsCellFormatter(cell, row, row.plan.id, "existing-recovery", translations, TagOptions),
      editor: {
        type: 'custom'
      },
      headerStyle: {
        width: '300px',
        textAlign: "center",
      },
      headerTitle: true,
      align: 'center',
      headerClasses: 'column-color-controls',
      classes: 'column-color-controls',
      cellStyle: {
        width: '300px',
        paddingBottom: '10px'
      }
    },
    {
      dataField: `${keyPrefix}likelihood`,
      text: translations.likelihood_of_unwanted_event,
      formatter: (cell, row, rowIndex) => likelihoodCellFormatter(cell, row, rowIndex, likelihoodOptions),
      editor: {
        type: 'dropdown'
      },
      headerStyle: {
        width: '220px',
        textAlign: "center",
      },
      title: true,
      align: 'center',
      headerClasses: 'column-color-likelihood-consequence',
      classes: 'column-color-likelihood-consequence',
    },
    {
      dataField: 'consequences_description',
      text: translations.consequence_text_description,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '300px',
        textAlign: "center",
      },
      cellInnerDivStyle: {
        height: "100%"
      },
      title: true,
      align: 'center',
      headerClasses: 'column-color-likelihood-consequence',
      classes: 'column-color-likelihood-consequence description',
    },
    ...ratingColumns,
    {
      dataField: 'current_risk_rating',
      text: translations.current_risk_rating,
      formatter: (cell, row) => riskRatingFormatter(cell, row, riskRatingDeps),
      editor: {
        type: 'custom'
      },
      headerStyle: {
        width: '120px',
        textAlign: "center",
      },
      title: false,
      align: 'center',
      headerClasses: 'column-color-risk-rating',
      classes: (cell, row) => riskRatingClasses(cell, row, riskRatingDeps),
    },
    {
      dataField: 'additional_preventative',
      text: translations.additional_preventative_controls,
      formatter: (cell, row)=>controlsCellFormatter(cell, row, row.plan.id, "additional-preventative", translations, TagOptions),
      editor: {
        type: 'custom'
      },
      headerStyle: {
        width: '300px',
        textAlign: "center",
      },
      headerTitle: true,
      align: 'center',
      headerClasses: 'column-color-controls',
      classes: 'column-color-controls',
      cellStyle: {
        width: '300px',
        paddingBottom: '10px'
      },
      hidden: true,
    },
    {
      dataField: 'additional_recovery',
      text: translations.additional_recovery_controls,
      formatter: (cell, row)=>controlsCellFormatter(cell, row, row.plan.id, "additional-recovery", translations, TagOptions),
      editor: {
        type: 'custom'
      },
      headerStyle: {
        width: '300px',
        textAlign: "center",
      },
      headerTitle: true,
      align: 'center',
      headerClasses: 'column-color-controls',
      classes: 'column-color-controls',
      cellStyle: {
        width: '300px',
        paddingBottom: '10px'
      },
      hidden: true,
    },
    {
      dataField: `${keyPrefix}new_likelihood`,
      text: translations.likelihood_of_unwanted_event,
      formatter: (cell, row, rowIndex) => likelihoodCellFormatter(cell, row, rowIndex, likelihoodOptions),
      editor: {
        type: 'dropdown'
      },
      headerStyle: {
        width: '220px',
        textAlign: "center",
      },
      title: true,
      align: 'center',
      headerClasses: 'column-color-likelihood-consequence',
      classes: 'column-color-likelihood-consequence',
      hidden: true,
    },
    ...newRatingColumns,
    {
      dataField: 'final_risk_rating',
      text: translations.final_risk_rating,
      formatter: (cell, row) => riskRatingFormatter(cell, row, { ...riskRatingDeps, isFinal: true }),
      editor: {
        type: 'custom'
      },
      headerStyle: {
        width: '120px',
        textAlign: "center",
      },
      title: false,
      align: 'center',
      headerClasses: 'column-color-risk-rating',
      classes: (cell, row) => riskRatingClasses(cell, row, { ...riskRatingDeps, isFinal: true }),
      hidden: true,
    },
    {
      dataField: 'actions',
      text: translations.numbered_1,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '300px',
      },
      cellInnerDivStyle: {
        height: "100%"
      },
      headerTitle: true,
      title: true,
      headerClasses: 'column-color-actions',
      classes: 'column-color-actions',
    },
    {
      dataField: 'manager',
      text: translations.numbered_2,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '300px',
      },
      headerTitle: true,
      title: true,
      headerClasses: 'column-color-actions',
      classes: 'column-color-actions',
    },
    {
      dataField: 'effectiveness',
      text: translations.numbered_3,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '300px',
      },
      cellInnerDivStyle: {
        height: "100%"
      },
      headerTitle: true,
      title: true,
      headerClasses: 'column-color-actions',
      classes: 'column-color-actions',
    },
    {
      dataField: 'status',
      text: translations.numbered_4,
      formatter: (cell, row) => actionStatusRenderer(cell, row, translations),
      editor: {
        type: 'custom'
      },
      headerStyle: {
        width: '300px',
      },
      headerTitle: true,
      headerClasses: 'column-color-actions',
      classes: 'column-color-actions',
    },
    {
      dataField: 'effectiveness2',
      text: translations.numbered_5,
      formatter: cellFormatter,
      editor: {
        type: 'textarea'
      },
      headerStyle: {
        width: '300px',
      },
      cellInnerDivStyle: {
        height: "100%"
      },
      headerTitle: true,
      title: true,
      headerClasses: 'column-color-actions',
      classes: 'column-color-actions',
    },
  ];
}


// WEBPACK FOOTER //
// ./src/components/risk_comparison_columns.js